import React, { useRef, useState, useEffect, useLayoutEffect } from "react";
import { SiReact, SiAngular, SiFlutter, SiTypescript, SiNodedotjs, SiThreedotjs, SiUnity, SiPython, SiCplusplus, SiRust } from "react-icons/si";
import rough from "roughjs/bundled/rough.esm.js";
import TerminalDetailOverlay from "./TerminalDetailOverlay";
import { TECH_DETAIL_CONFIG, toTechKey } from "./TechStackDetailConfig";

// Standard Tech Stack Daten
const defaultTechStack = [
  { name: "React", icon: SiReact, color: "#61DAFB" },
  { name: "Angular", icon: SiAngular, color: "#DD0031" },
  { name: "Flutter", icon: SiFlutter, color: "#02569B" },
  { name: "TypeScript", icon: SiTypescript, color: "#3178C6" },
  { name: "Node.js", icon: SiNodedotjs, color: "#339933" },
  { name: "Three.js", icon: SiThreedotjs, color: "#000000", dark: true },
];

// Erweiterter Tech Stack (mit Unity & Python)
export const extendedTechStack = [
  ...defaultTechStack,
  { name: "Unity", icon: SiUnity, color: "#000000", dark: true },
  { name: "Python", icon: SiPython, color: "#3776AB" },
  { name: "C++", icon: SiCplusplus, color: "#00599C" },
  { name: "Rust", icon: SiRust, color: "#CE422B" },
];

// Section Badge (wie in About)
const SectionBadge = ({ label }) => (
  <div className="flex justify-center mb-3">
    <span className="inline-flex items-center gap-2 rounded-md border border-brand px-4 py-1 text-[10px] uppercase tracking-[0.4em] font-mono font-semibold text-brand bg-[#151515]">
      <span className="h-2.5 w-2.5 rounded-full bg-brand" />
      {label}
    </span>
  </div>
);

// RoughCircle für Tech Stack Icons
function TechRoughCircle({ size = 56, color = "#AC8E66", strokeWidth = 1.5, roughness = 1.5, className = "" }) {
  const svgRef = useRef(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useLayoutEffect(() => {
    if (!isClient || !svgRef.current) return;
    svgRef.current.innerHTML = "";
    const rc = rough.svg(svgRef.current);
    const padding = strokeWidth;
    const node = rc.circle(size / 2, size / 2, size - padding * 2, {
      roughness,
      stroke: color,
      strokeWidth,
      fill: "none",
    });
    svgRef.current.appendChild(node);
  }, [isClient, size, color, strokeWidth, roughness]);

  if (!isClient) return null;

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      className={`absolute inset-0 pointer-events-none ${className}`}
    />
  );
}

// Einzelnes Tech Icon
function TechIcon({ name, icon: Icon, color, dark = false, onClick, isInteractive = false, darkTheme = false }) {
  const iconColor = darkTheme && dark ? "#EFEBDC" : color;
  const [hovered, setHovered] = useState(false);
  const bg = darkTheme ? "#1a1a1a" : hovered ? "#1a1a1a" : "#f0ede1";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!isInteractive}
      aria-label={isInteractive ? `${name} Details oeffnen` : undefined}
      className={`flex flex-col items-center gap-2 ${isInteractive ? "cursor-pointer" : "cursor-default"}`}
    >
      <div
        className="relative w-[72px] h-[72px] sm:w-[80px] sm:h-[80px]
        flex items-center justify-center
        group
         rounded-full
        shadow-md transition-colors duration-200"
        style={{ backgroundColor: bg }}
        title={name}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Mobile circle */}
        <TechRoughCircle size={72} color="#AC8E66" strokeWidth={1.2} roughness={0.8} className="sm:hidden" />
        {/* Desktop circle */}
        <TechRoughCircle size={80} color="#AC8E66" strokeWidth={1.2} roughness={0.8} className="hidden sm:block" />
        <Icon
          className="w-7 h-7 sm:w-8 sm:h-8 opacity-70 group-hover:opacity-100 transition-opacity"
          style={{ color: iconColor }}
        />
      </div>
      <span className="text-xs font-mono" style={{ color: darkTheme ? "#EFEBDC" : "#475569" }}>
        {name}
      </span>
    </button>
  );
}

/**
 * TechStack Komponente
 * @param {Object} props
 * @param {Array} props.items - Array von Tech Items { name, icon, color }. Default: defaultTechStack
 * @param {boolean} props.extended - Wenn true, wird extendedTechStack verwendet (inkl. Unity & Python)
 * @param {boolean} props.showLabel - Zeigt einfaches "Tech Stack" Label über den Icons
 * @param {boolean} props.showBadge - Zeigt SectionBadge (wie in About)
 * @param {string} props.className - Zusätzliche CSS Klassen
 */
export default function TechStack({
  items,
  extended = false,
  showLabel = false,
  showBadge = false,
  darkTheme = false,
  enableDetailOverlay = false,
  detailCtaLabel = "saghallo",
  detailCtaMode = "inlineContact",
  className = ""
}) {
  const techItems = items || (extended ? extendedTechStack : defaultTechStack);
  const [activeTechKey, setActiveTechKey] = useState(null);
  const orderedTechKeys = techItems
    .map((tech) => toTechKey(tech.name))
    .filter((key, index, arr) => arr.indexOf(key) === index && Boolean(TECH_DETAIL_CONFIG[key]));
  const activeTechIndex = activeTechKey ? orderedTechKeys.indexOf(activeTechKey) : -1;
  const activeTechDetail = activeTechKey ? TECH_DETAIL_CONFIG[activeTechKey] : null;

  const openTechDetail = (techName) => {
    if (!enableDetailOverlay) return;
    const key = toTechKey(techName);
    if (!TECH_DETAIL_CONFIG[key]) return;
    setActiveTechKey(key);
  };
  const handlePrev = () => {
    if (activeTechIndex <= 0) return;
    setActiveTechKey(orderedTechKeys[activeTechIndex - 1]);
  };
  const handleNext = () => {
    if (activeTechIndex < 0 || activeTechIndex >= orderedTechKeys.length - 1) return;
    setActiveTechKey(orderedTechKeys[activeTechIndex + 1]);
  };

  return (
    <div className={className}>
      {showBadge && (
        <div className="text-center mb-10">
          <SectionBadge label="Tech Stack" />
        </div>
      )}
      {showLabel && !showBadge && (
        <div className="text-center mb-10">
          <p className="text-xs text-brand uppercase tracking-wider mb-2">Tech Stack</p>
        </div>
      )}
      <div className="flex flex-wrap justify-center gap-6 sm:gap-8">
        {techItems.map((tech) => (
          <TechIcon
            key={tech.name}
            {...tech}
            darkTheme={darkTheme}
            isInteractive={enableDetailOverlay}
            onClick={() => openTechDetail(tech.name)}
          />
        ))}
      </div>
      <TerminalDetailOverlay
        isOpen={Boolean(activeTechDetail)}
        onClose={() => setActiveTechKey(null)}
        title={activeTechDetail?.title || "TechStack"}
        subtitle={activeTechDetail?.subtitle || "ls techstack/"}
        lines={activeTechDetail?.lines || []}
        storageKey={activeTechDetail?.storageKey}
        ctaLabel={detailCtaLabel}
        ctaMode={detailCtaMode}
        onPrev={handlePrev}
        onNext={handleNext}
        hasPrev={activeTechIndex > 0}
        hasNext={activeTechIndex >= 0 && activeTechIndex < orderedTechKeys.length - 1}
      />
    </div>
  );
}

// Export für direkte Nutzung
export { defaultTechStack, TechRoughCircle, TechIcon, SectionBadge };
